import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AiChatPauseState,
  AiSettingsService,
  saveErrorMessage,
  GlobalUsageLimit,
  SystemPromptResponse,
} from '../../Services/ai-settings.service';
import {
  catchError,
  forkJoin,
  interval,
  map,
  of,
  retry,
  Subscription,
  switchMap,
  take,
  timeout,
} from 'rxjs';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

/** The breaker probes once a minute; asking faster learns nothing new. */
const STATUS_POLL_MS = 30_000;

/**
 * What the field starts at before anyone has set a budget. 75 % leaves about a
 * quarter of the week's spend after the warning - enough to finish what you
 * were doing, where 80 % is too thin to act on and 70 % cries wolf.
 */
const DEFAULT_WARN_AT_PERCENT = 75;

@Component({
  selector: 'app-ai-settings-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatSlideToggleModule,
  ],
  templateUrl: './ai-settings-page.component.html',
  styleUrls: ['./ai-settings-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiSettingsPageComponent implements OnInit, OnDestroy {
  openInfo: string | null = null;

  toggleInfo(section: string): void {
    this.openInfo = this.openInfo === section ? null : section;
  }
  systemPrompt = '';
  initialSystemPrompt = '';
  selectedModel: string | null = null;
  initialModel = '';
  availableModels: string[] = [];
  promptVariables: string[] = [];
  loading = true;
  loadError: string | null = null;
  /** Empty values are only meaningful after the corresponding read succeeded. */
  promptStatus: 'unknown' | 'ok' = 'unknown';
  modelStatus: 'unknown' | 'missing' | 'ok' = 'unknown';

  /**
   * The weekly budget every company gets. Null means none is set, which is the
   * shipped state and means nothing is enforced for anyone - a distinction the
   * card has to draw, because a zero would read as the opposite.
   */
  usageLimit: GlobalUsageLimit | null = null;
  /** Distinguishes an unset budget from an unavailable quota endpoint. */
  usageLimitStatus: 'ok' | 'unsupported' | 'error' = 'ok';
  budgetUsd: number | null = null;
  warnAtPercent: number | null = null;
  savingBudget = false;
  budgetSaved = false;
  budgetError: string | null = null;
  savingPrompt = false;
  saveError: string | null = null;
  promptSaved = false;
  savingModel = false;
  modelSaveError: string | null = null;
  modelSaved = false;

  /**
   * The kill switch. Stops the AI chat for every customer at once, without a
   * deploy - the same reach the model and the prompt already have from here.
   *
   * It exists because a pause is not always the provider's fault: the agent may
   * be booking something wrong, or a prompt change may have gone badly, and
   * waiting for a release to stop it is waiting too long.
   */
  pause: AiChatPauseState = { paused: false };
  pauseNote = '';
  savingPause = false;
  pauseError: string | null = null;
  /**
   * Three readings, because two would make the page lie in one of them.
   *
   * 'unsupported' is the 404: the portal ships ahead of wrebit-bookkeep often
   * enough that this is a real state, and a control that cannot work must not
   * be offered as if it could. Anything else is 'error' - a 500 or a dropped
   * connection says nothing about whether the endpoint exists, and telling an
   * admin the feature is missing from this environment when it is merely
   * failing sends them after the wrong problem. That one is retryable; a 404
   * is not, until something is deployed.
   */
  pauseStatus: 'ok' | 'unsupported' | 'error' = 'ok';
  pauseLoadError: string | null = null;
  retryingPause = false;
  /**
   * A poll that failed. The reading on screen is then the last one we got, not
   * the current one, and the row has to say so - a status that is quietly stale
   * is the one way this card can be worse than not existing.
   */
  statusStale = false;
  /** When the last reading arrived here. Observed by us, not reported by them. */
  statusReadAt: string | null = null;
  promptSavedAt: string | null = null;
  modelChangedAt: string | null = null;
  /**
   * Which stamp we saved and then could not read back.
   *
   * Without it a failed read-back looks exactly like a backend that keeps no
   * timestamps - both render as no stamp at all - and one blip would hide the
   * time for the rest of the session underneath a message saying the save
   * worked. The same confusion between "absent" and "unknown" this card keeps
   * having to be talked out of.
   */
  stampRefreshFailed: 'prompt' | 'model' | null = null;

  private statusPoll?: Subscription;
  /**
   * The pause write outlives the view if someone navigates away mid-save, and
   * its callback restarts the poll. Without this the page would leave an
   * interval running against a component nobody is looking at.
   */
  private destroyed = false;

  constructor(
    private aiSettingsService: AiSettingsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    forkJoin({
      // Caught like the other two, and for a sharper reason: this was the only
      // source in the forkJoin without a catchError, so one slow or failing
      // request took the whole page down - and since a TimeoutError carries no
      // status, the handler below rendered "Något gick fel (undefined)" after
      // ten seconds of waiting. A missing prompt is worth saying; it is not
      // worth losing drift, the model and the budget over.
      response: this.aiSettingsService.getSystemPrompt().pipe(
        take(1),
        timeout(10000),
        map((state) => ({ state, error: null as HttpErrorResponse | null })),
        catchError((err: HttpErrorResponse) =>
          of({ state: null as SystemPromptResponse | null, error: err }),
        ),
      ),
      // Caught here rather than alongside the prompt: an older backend answers
      // 404 to this, and a forkJoin that lets it through takes the whole page
      // with it - the prompt and the model would stop loading because of a
      // control that had not shipped yet.
      pause: this.aiSettingsService.getPause().pipe(
        take(1),
        timeout(10000),
        map((state) => ({ state, error: null as HttpErrorResponse | null })),
        catchError((err: HttpErrorResponse) =>
          of({ state: null as AiChatPauseState | null, error: err }),
        ),
      ),
      // Same treatment, same reason: this endpoint is newer than the page.
      usageLimit: this.aiSettingsService.getUsageLimit().pipe(
        take(1),
        timeout(10000),
        map((res) => ({
          limit: res.limit,
          error: null as HttpErrorResponse | null,
        })),
        catchError((err: HttpErrorResponse) =>
          of({ limit: null as GlobalUsageLimit | null, error: err }),
        ),
      ),
    })
      .pipe(take(1))
      .subscribe({
        next: ({ response, pause, usageLimit }) => {
          this.applyPauseLoad(pause.state, pause.error);
          this.applyUsageLimitLoad(usageLimit.limit, usageLimit.error);
          this.applyPromptLoad(response.state, response.error);
          this.startStatusPolling();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          this.loadError =
            err.status === 0
              ? 'Kunde inte nå servern. Kontrollera att backend körs.'
              : `Något gick fel (${err.status}). Försök ladda om sidan.`;
          this.cdr.markForCheck();
        },
      });
  }

  onSaveSystemPrompt(): void {
    this.savingPrompt = true;
    this.promptSaved = false;
    this.saveError = null;
    this.stampRefreshFailed = null;
    this.aiSettingsService
      .saveSystemPrompt(this.systemPrompt)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.savingPrompt = false;
          this.promptSaved = true;
          this.initialSystemPrompt = this.systemPrompt;
          // Cleared rather than set from this clock. The save answers 204, so
          // the only instant we could show here is the browser's, which is not
          // the one the row got and is absent entirely on a backend that does
          // not keep it. Blank for the moment it takes to read the real one.
          this.promptSavedAt = null;
          this.refreshSavedState('prompt');
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.savingPrompt = false;
          this.saveError = saveErrorMessage(
            err,
            `Något gick fel (${err.status}). Försök igen.`
          );
          this.cdr.markForCheck();
        },
      });
  }

  onSaveModel(): void {
    this.savingModel = true;
    this.modelSaved = false;
    this.modelSaveError = null;
    this.stampRefreshFailed = null;
    this.aiSettingsService
      .saveModel(this.selectedModel)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.savingModel = false;
          this.modelSaved = true;
          this.modelChangedAt = null;
          this.refreshSavedState('model');
          this.initialModel = this.selectedModel ?? '';
          this.modelStatus = this.initialModel ? 'ok' : 'missing';
          this.selectedModel = null;
          this.availableModels = this.buildModelOptions(
            this.initialModel,
            this.availableModels,
          );
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.savingModel = false;
          this.modelSaveError = saveErrorMessage(
            err,
            `Något gick fel (${err.status}). Försök igen.`
          );
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * One control for both directions. Pausing sends the note; resuming does not
   * - the note describes why the chat was stopped, and carrying it onto the
   * record that it started again would misattribute it.
   */
  onTogglePause(): void {
    const next = !this.pause.paused;
    this.savingPause = true;
    this.pauseError = null;
    // Stopped for the duration of the write. A poll already in flight would
    // answer with the state from before the toggle and, arriving after it,
    // overwrite the state the server just confirmed - the row would then read
    // "Agenten kör" on an agent that is paused, for up to half a minute, which
    // is the one thing this card must never do. Restarted on the way out, so
    // the next reading is a full interval after the change rather than racing
    // it.
    this.stopStatusPolling();

    this.aiSettingsService
      .setPause(next, next ? this.pauseNote : undefined)
      .pipe(take(1))
      .subscribe({
        next: (state) => {
          this.pause = state;
          this.pauseNote = '';
          this.savingPause = false;
          this.statusStale = false;
          this.statusReadAt = new Date().toISOString();
          this.startStatusPolling();
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.savingPause = false;
          // The backend's own message wins where it has one. A failed pause is
          // the worst place to swallow it: "något gick fel" leaves the admin
          // guessing at the exact moment they are trying to stop the agent.
          this.pauseError = saveErrorMessage(
            err,
            `Något gick fel (${err.status}). Försök igen.`
          );
          this.startStatusPolling();
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Retry for the failure that deserves one. A 404 is not offered a button:
   * nothing the admin can do from here makes the endpoint appear.
   */
  onRetryPause(): void {
    this.retryingPause = true;
    this.pauseLoadError = null;
    this.aiSettingsService
      .getPause()
      .pipe(
        take(1),
        map((state) => ({ state, error: null as HttpErrorResponse | null })),
        catchError((err: HttpErrorResponse) =>
          of({ state: null as AiChatPauseState | null, error: err }),
        ),
      )
      .subscribe(({ state, error }) => {
        this.retryingPause = false;
        this.applyPauseLoad(state, error);
        this.startStatusPolling();
        this.cdr.markForCheck();
      });
  }

  /**
   * One place that decides which of the three readings we have, so the load and
   * the retry cannot come to different conclusions about the same response.
   */
  private applyPauseLoad(
    state: AiChatPauseState | null,
    error: HttpErrorResponse | null,
  ): void {
    if (state) {
      this.pauseStatus = 'ok';
      this.pause = state;
      this.pauseLoadError = null;
      this.statusStale = false;
      this.statusReadAt = new Date().toISOString();
      return;
    }

    this.pause = { paused: false };
    this.statusReadAt = null;
    this.statusStale = false;

    if (error?.status === 404) {
      this.pauseStatus = 'unsupported';
      this.pauseLoadError = null;
      return;
    }

    this.pauseStatus = 'error';
    this.pauseLoadError =
      error?.status === 401 || error?.status === 403
        ? 'Du saknar behörighet att läsa driftläget. Logga in igen eller kontrollera backendens JWT-konfiguration.'
        : error?.status === 0
        ? 'Kunde inte nå servern.'
        : `Kunde inte läsa driftläget (${error?.status ?? 'okänt fel'}).`;
  }

  /**
   * What the server has, after we have told it something. Read back rather
   * than assumed, on all three counts.
   *
   * The timestamps because save answers 204: an instant made here would be the
   * browser's, not the one the row got, and on a backend that keeps no
   * timestamp it would be a number invented out of nothing.
   *
   * The model because the authoritative value is in this same response and we
   * were throwing it away in favour of the one we had just sent. A model
   * change that quietly reverted is the case the stamp exists to catch, and
   * reading `current` back shows it outright rather than leaving an admin to
   * infer it from a date that looks older than it should.
   *
   * A failure here is not an absent timestamp. `pending` is the stamp we
   * cleared on the way in, and therefore the one we cannot account for if the
   * read never comes back.
   */
  private refreshSavedState(pending: 'prompt' | 'model'): void {
    this.aiSettingsService
      .getSystemPrompt()
      .pipe(
        take(1),
        // Two more tries before giving up. The save itself has already landed;
        // this is only the read of what it wrote, and a blip on that is not
        // worth telling anyone about if the next attempt succeeds.
        retry({ count: 2, delay: 1_000 }),
        catchError(() => of(null)),
      )
      .subscribe((response) => {
        if (!response) {
          this.stampRefreshFailed = pending;
          if (pending === 'model') this.modelStatus = 'unknown';
          this.cdr.markForCheck();
          return;
        }
        this.stampRefreshFailed = null;
        this.promptSavedAt = response.promptSavedAt ?? null;
        this.modelChangedAt = response.model?.changedAt ?? null;
        this.initialModel = response.model?.current ?? '';
        this.modelStatus = this.initialModel ? 'ok' : 'missing';
        this.availableModels = this.buildModelOptions(
          this.initialModel,
          this.availableModels,
        );
        this.cdr.markForCheck();
      });
  }

  get promptStampUnknown(): boolean {
    return this.stampRefreshFailed === 'prompt';
  }

  get modelStampUnknown(): boolean {
    return this.stampRefreshFailed === 'model';
  }

  get pauseSupported(): boolean {
    return this.pauseStatus === 'ok';
  }

  get pausedSinceLabel(): string | null {
    return this.formatInstant(this.pause.paused ? this.pause.since : null);
  }

  /**
   * When the agent was last resumed, which is only ever set once someone has
   * paused it at least once.
   *
   * The backend used to answer a resume with a bare { paused: false } and no
   * timestamp, so this reads null against an older one - which is the same
   * thing it reads when nothing has ever been paused, and both should show
   * nothing rather than a guess.
   */
  get resumedSinceLabel(): string | null {
    return this.formatInstant(this.pause.paused ? null : this.pause.since);
  }

  get promptSavedLabel(): string | null {
    return this.formatInstant(this.promptSavedAt);
  }

  get modelChangedLabel(): string | null {
    return this.formatInstant(this.modelChangedAt);
  }

  /**
   * Absent is its own answer. A backend with the pause endpoint but without the
   * breaker exposed - the state between d903af14 and c4f7db8a - sends no
   * provider at all, and reading that as 'ok' would put "Leverantören svarar
   * normalt" on a page that has never been told anything about the provider.
   */
  get providerState(): 'ok' | 'down' | 'unknown' {
    return this.pause.provider?.status ?? 'unknown';
  }

  get providerDown(): boolean {
    return this.providerState === 'down';
  }

  get providerSinceLabel(): string | null {
    return this.formatInstant(this.pause.provider?.since ?? null);
  }

  /**
   * Which of the three the status row shows. A manual pause outranks an open
   * breaker: it is the one a person controls and the one that does not lift
   * itself, so it is the more useful thing to read first.
   */
  get runStatus(): 'running' | 'paused' | 'provider-down' | 'unknown' {
    if (this.pauseStatus !== 'ok') return 'unknown';
    if (this.pause.paused) return 'paused';
    if (this.providerDown) return 'provider-down';
    return 'running';
  }

  /**
   * Cheap on the server - it reads the breaker's own state and never calls the
   * provider - and a status row that is stale is worse than none.
   */
  private startStatusPolling(): void {
    if (this.destroyed || this.pauseStatus !== 'ok' || this.statusPoll) {
      return;
    }
    this.statusPoll = interval(STATUS_POLL_MS)
      .pipe(
        switchMap(() =>
          this.aiSettingsService.getPause().pipe(catchError(() => of(null))),
        ),
      )
      .subscribe((pause) => {
        // A failed poll leaves the last reading on screen - there is nothing
        // better to show - but it stops being presented as current. Silently
        // keeping it would mean the card goes on saying the provider answers
        // normally long after it has stopped being able to tell.
        this.statusStale = !pause;
        if (pause) {
          this.pause = pause;
          this.statusReadAt = new Date().toISOString();
        }
        this.cdr.markForCheck();
      });
  }

  private stopStatusPolling(): void {
    this.statusPoll?.unsubscribe();
    this.statusPoll = undefined;
  }

  get statusReadLabel(): string | null {
    return this.formatInstant(this.statusReadAt);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopStatusPolling();
  }

  // Explicit fields rather than dateStyle/timeStyle: this project's TS lib
  // target predates those options.
  private formatInstant(instant: string | null | undefined): string | null {
    if (!instant) {
      return null;
    }
    return new Date(instant).toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  onSystemPromptChange(): void {
    this.promptSaved = false;
  }

  onSelectedModelChange(): void {
    this.modelSaved = false;
  }

  get hasUnsavedChanges(): boolean {
    return this.systemPrompt !== this.initialSystemPrompt;
  }

  get hasUnsavedModelChange(): boolean {
    return !!this.selectedModel && this.selectedModel !== this.initialModel;
  }

  get promptCharacterCount(): number {
    return this.systemPrompt.length;
  }

  get promptWordCount(): number {
    return this.systemPrompt.trim()
      ? this.systemPrompt.trim().split(/\s+/).length
      : 0;
  }

  get variableCount(): number {
    return this.promptVariables.length;
  }

  get currentModelLabel(): string {
    if (this.modelStatus === 'unknown') return 'Kunde inte läsas';
    if (this.modelStatus === 'missing') return 'Ingen modell vald';
    return this.initialModel;
  }

  private buildModelOptions(
    currentModel: string | null | undefined,
    availableModels: string[] | null | undefined,
  ): string[] {
    const models = Array.isArray(availableModels) ? [...availableModels] : [];

    if (currentModel && !models.includes(currentModel)) {
      models.unshift(currentModel);
    }

    return models;
  }

  /**
   * Everything the prompt request carries, or the fact that it did not arrive.
   *
   * loadError is still set when this one fails, because a page that cannot read
   * the prompt genuinely cannot do its main job - but it is set here, after the
   * other two have been applied, so drift and the budget are still on screen
   * behind the message.
   */
  private applyPromptLoad(
    response: SystemPromptResponse | null,
    error: HttpErrorResponse | null,
  ): void {
    if (!response) {
      this.promptStatus = 'unknown';
      this.modelStatus = 'unknown';
      this.loadError =
        error?.status === 401 || error?.status === 403
          ? 'Du saknar behörighet att läsa AI-inställningarna. Logga in igen eller kontrollera backendens JWT-konfiguration.'
          : error && typeof error.status === 'number' && error.status !== 0
          ? `Inställningarna kunde inte läsas (${error.status}). Försök ladda om sidan.`
          : 'Inställningarna kunde inte läsas. Kontrollera att backend svarar.';
      return;
    }

    this.promptStatus = 'ok';
    this.promptSavedAt = response.promptSavedAt ?? null;
    this.modelChangedAt = response.model.changedAt ?? null;
    this.systemPrompt = response.prompt ?? '';
    this.initialSystemPrompt = this.systemPrompt;
    this.initialModel = response.model.current ?? '';
    this.modelStatus = this.initialModel ? 'ok' : 'missing';
    this.availableModels = this.buildModelOptions(
      response.model.current,
      response.model.available,
    );
    this.promptVariables = response.promptVariables;
  }

  private applyUsageLimitLoad(
    limit: GlobalUsageLimit | null,
    error: HttpErrorResponse | null,
  ): void {
    if (error) {
      // 404 is an older backend, not a fault. Anything else is, but it must not
      // take the page with it - the prompt and the model are still readable.
      this.usageLimitStatus = error.status === 404 ? 'unsupported' : 'error';
      if (this.usageLimitStatus === 'error') {
        this.budgetError =
          error.status === 401 || error.status === 403
            ? 'Du saknar behörighet att läsa veckogränsen.'
            : 'Veckogränsen kunde inte läsas.';
      }
      return;
    }

    this.usageLimitStatus = 'ok';
    this.usageLimit = limit;
    this.budgetUsd = limit?.weeklyCostLimitUsd ?? null;
    this.warnAtPercent = limit?.warnAtPercent ?? DEFAULT_WARN_AT_PERCENT;
  }

  get hasUnsavedBudgetChange(): boolean {
    if (!this.usageLimit) return this.budgetUsd !== null;
    return (
      this.budgetUsd !== this.usageLimit.weeklyCostLimitUsd ||
      this.warnAtPercent !== this.usageLimit.warnAtPercent
    );
  }

  onBudgetChange(): void {
    this.budgetSaved = false;
    this.budgetError = null;
  }

  saveBudget(): void {
    const budget = Number(this.budgetUsd);
    const warnAt = Number(this.warnAtPercent);

    if (!Number.isFinite(budget) || budget <= 0) {
      this.budgetError = 'Ange en budget större än 0.';
      this.cdr.markForCheck();
      return;
    }
    // Both ends are refused rather than stored: 0 would warn from the first
    // message, and 100 would warn only once the week was already over.
    if (!Number.isFinite(warnAt) || warnAt <= 0 || warnAt >= 100) {
      this.budgetError = 'Varningströskeln måste ligga mellan 1 och 99.';
      this.cdr.markForCheck();
      return;
    }

    this.savingBudget = true;
    this.budgetError = null;
    this.budgetSaved = false;
    this.cdr.markForCheck();

    this.aiSettingsService
      .saveUsageLimit({ weeklyCostLimitUsd: budget, warnAtPercent: warnAt })
      .pipe(take(1))
      .subscribe({
        next: (saved) => {
          this.usageLimit = {
            ...saved,
            reason: saved.reason ?? null,
            changedBy: saved.changedBy ?? null,
          };
          this.budgetUsd = saved.weeklyCostLimitUsd;
          this.warnAtPercent = saved.warnAtPercent;
          this.savingBudget = false;
          this.budgetSaved = true;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.savingBudget = false;
          this.budgetError = saveErrorMessage(
            err,
            'Budgeten kunde inte sparas. Försök igen.',
          );
          this.cdr.markForCheck();
        },
      });
  }
}
